import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import TrainIcon from "@mui/icons-material/Train";
import ScienceIcon from "@mui/icons-material/Science";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { GroupedVirtuoso, type GroupedVirtuosoHandle } from "react-virtuoso";
import { useAppData, type Domain, type Project } from "store/appData";
import { useDatasetDetail } from "./DatasetDetailContext";
import {
  formatProjectLabel,
  useDatasetViewerSettings,
} from "../hooks/useDatasetViewerSettings";
import { type DatasetImage } from "../api";
import t from "../translations";

interface AnnotationTarget {
  url: string;
  domainName: string;
}

function resolveAnnotationTarget(
  image: DatasetImage,
  projects: Record<number, Project>,
  domains: Record<number, Domain>,
): AnnotationTarget | null {
  const project = projects[image.project_id];
  if (!project?.primary_domain_id) return null;
  const domain = domains[project.primary_domain_id];
  if (!domain) return null;
  const trimmed = domain.url.replace(/\/+$/, "");
  const withScheme = /^https?:\/\//.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  return {
    url: `${withScheme}/${image.project_id}/${image.id}`,
    domainName: domain.name,
  };
}

// Images bucketed under their project, in the order each project first
// appears in the (image-id-ordered) list — a stable order that does not
// reshuffle as more images load.
interface ProjectGroup {
  projectId: number;
  images: DatasetImage[];
}

export default function DatasetImageList() {
  const params = useParams<{ datasetId: string; imageId: string }>();
  const datasetId = params.datasetId ?? "";
  const activeImageId = params.imageId ? parseInt(params.imageId) : -1;

  const { projects, domains } = useAppData();
  const { projectNameDisplay } = useDatasetViewerSettings();
  const {
    datasetName,
    totalImageCount,
    projectCounts,
    images,
    detailBasePath,
    kind,
  } = useDatasetDetail();

  const virtuosoRef = useRef<GroupedVirtuosoHandle>(null);
  const navigate = useNavigate();
  const [projectsExpanded, setProjectsExpanded] = useState(false);
  // Project ids whose accordion is open. Empty = every project collapsed,
  // which is the state a dataset opens in.
  const [expandedProjects, setExpandedProjects] = useState<Set<number>>(
    () => new Set(),
  );

  const projectLabelMap = useMemo(
    () =>
      new Map(
        Object.values(projects).map((p) => [
          p.id,
          formatProjectLabel(p, projectNameDisplay),
        ]),
      ),
    [projects, projectNameDisplay],
  );

  // Full per-project image counts from the dataset detail, so an accordion
  // header shows the real total even before every page has loaded.
  const projectTotalMap = useMemo(
    () => new Map(projectCounts.map(({ id, count }) => [id, count])),
    [projectCounts],
  );

  const groups = useMemo<ProjectGroup[]>(() => {
    const order: number[] = [];
    const byProject = new Map<number, DatasetImage[]>();
    for (const image of images) {
      let bucket = byProject.get(image.project_id);
      if (!bucket) {
        bucket = [];
        byProject.set(image.project_id, bucket);
        order.push(image.project_id);
      }
      bucket.push(image);
    }
    return order.map((projectId) => ({
      projectId,
      images: byProject.get(projectId) as DatasetImage[],
    }));
  }, [images]);

  // Items of expanded groups only, concatenated in group order — this is the
  // sequence GroupedVirtuoso's flat `itemContent` index addresses.
  const flatImages = useMemo(
    () =>
      groups.flatMap((group) =>
        expandedProjects.has(group.projectId) ? group.images : [],
      ),
    [groups, expandedProjects],
  );

  // A collapsed project contributes 0 items, so GroupedVirtuoso renders just
  // its header.
  const groupCounts = useMemo(
    () =>
      groups.map((group) =>
        expandedProjects.has(group.projectId) ? group.images.length : 0,
      ),
    [groups, expandedProjects],
  );

  const toggleProject = (projectId: number) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };

  useEffect(() => {
    if (activeImageId >= 0 || !images.length) return;
    navigate(`${detailBasePath}/${datasetId}/${images[0].id}`, {
      replace: true,
    });
  }, [activeImageId, images, navigate, detailBasePath, datasetId]);

  // Keep the list scrolled to the active image. If its project accordion is
  // collapsed, bring the project header into view rather than forcing it open.
  useEffect(() => {
    if (activeImageId < 0) return;
    const itemIndex = flatImages.findIndex((img) => img.id === activeImageId);
    if (itemIndex >= 0) {
      virtuosoRef.current?.scrollToIndex({ index: itemIndex, align: "center" });
      return;
    }
    const activeImage = images.find((img) => img.id === activeImageId);
    if (!activeImage) return;
    const groupIndex = groups.findIndex(
      (group) => group.projectId === activeImage.project_id,
    );
    if (groupIndex >= 0) {
      virtuosoRef.current?.scrollToIndex({ groupIndex, align: "center" });
    }
  }, [activeImageId, flatImages, images, groups]);

  useEffect(() => {
    if (activeImageId < 0 || !images.length) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const target = event.target as HTMLElement | null;
      if (
        target?.isContentEditable ||
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.tagName === "SELECT"
      ) {
        return;
      }

      const index = images.findIndex((img) => img.id === activeImageId);
      if (index < 0) return;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        const next = images[index + 1];
        if (next) {
          navigate(`${detailBasePath}/${datasetId}/${next.id}`);
        }
      } else {
        event.preventDefault();
        const prev = images[index - 1];
        if (prev) {
          navigate(`${detailBasePath}/${datasetId}/${prev.id}`);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeImageId, images, navigate, detailBasePath, datasetId]);

  const handleRowClick = (image: DatasetImage) => {
    navigate(`${detailBasePath}/${datasetId}/${image.id}`);
  };

  const hasProjects = projectCounts.length > 0;
  const imageCountText = datasetName
    ? t.ImageCount(totalImageCount)
    : t.LoadingImages;
  const projectCountText = t.ProjectCount(projectCounts.length);

  return (
    <Stack
      sx={{
        height: "100%",
        width: 320,
        flexShrink: 0,
        bgcolor: "background.paper",
        borderRight: 1,
        borderColor: "divider",
      }}
      data-testid="datasetImageList.container"
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Stack
          sx={{
            flexDirection: "row",
            alignItems: "center",
            gap: 0.5,
            cursor: hasProjects ? "pointer" : "default",
            userSelect: "none",
            color: "text.secondary",
          }}
          onClick={() => hasProjects && setProjectsExpanded((prev) => !prev)}
          data-testid="datasetImageList.projectCountToggle"
        >
          <Typography variant="caption">{imageCountText}</Typography>
          {hasProjects && (
            <>
              <Typography variant="caption">·</Typography>
              <Typography variant="caption">{projectCountText}</Typography>
              {projectsExpanded ? (
                <ExpandLessIcon fontSize="inherit" />
              ) : (
                <ExpandMoreIcon fontSize="inherit" />
              )}
            </>
          )}
        </Stack>
        {hasProjects && projectsExpanded && (
          <Box
            sx={{ mt: 0.5, pt: 0.5, maxHeight: "50vh", overflowY: "auto" }}
            data-testid="datasetImageList.projectBreakdown"
          >
            {projectCounts.map(({ id, count }, index) => {
              const pct =
                totalImageCount > 0
                  ? Math.round((count / totalImageCount) * 100)
                  : 0;
              return (
                <Stack
                  key={id}
                  sx={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    gap: 1,
                    px: 0.5,
                    py: 0.25,
                    fontSize: 11,
                    color: "text.secondary",
                    bgcolor: index % 2 === 0 ? "action.hover" : "transparent",
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {projectLabelMap.get(id) ?? t.UnknownProject(id)}
                  </Box>
                  <Stack
                    sx={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Box component="span">{count.toLocaleString()}</Box>
                    <Box
                      component="span"
                      sx={{
                        color: "text.disabled",
                        fontSize: 10,
                        width: 32,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {pct}%
                    </Box>
                  </Stack>
                </Stack>
              );
            })}
          </Box>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <GroupedVirtuoso
          ref={virtuosoRef}
          style={{ width: "100%", height: "100%" }}
          groupCounts={groupCounts}
          groupContent={(groupIndex) => {
            const group = groups[groupIndex];
            if (!group) return null;
            const expanded = expandedProjects.has(group.projectId);
            const label =
              projectLabelMap.get(group.projectId) ??
              t.UnknownProject(group.projectId);
            const count =
              projectTotalMap.get(group.projectId) ?? group.images.length;
            return (
              <Stack
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onClick={() => toggleProject(group.projectId)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleProject(group.projectId);
                  }
                }}
                data-testid={`datasetImageList.projectAccordion.${group.projectId}`}
                sx={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1.5,
                  py: 0.75,
                  cursor: "pointer",
                  userSelect: "none",
                  bgcolor: "background.paper",
                  borderBottom: 1,
                  borderColor: "divider",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                {expanded ? (
                  <ExpandLessIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                ) : (
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {count.toLocaleString()}
                </Typography>
              </Stack>
            );
          }}
          itemContent={(index) => {
            const image = flatImages[index];
            if (!image) return null;
            const active = image.id === activeImageId;
            const annotation = resolveAnnotationTarget(
              image,
              projects,
              domains,
            );
            return (
              <Stack
                sx={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 0.625,
                  mb: "1px",
                  cursor: active ? "default" : "pointer",
                  bgcolor: active ? "primary.main" : "transparent",
                  color: active ? "primary.contrastText" : "text.primary",
                  fontWeight: active ? 700 : 400,
                  "&:hover": active ? undefined : { bgcolor: "action.hover" },
                }}
                role={active ? undefined : "button"}
                tabIndex={active ? -1 : 0}
                onClick={() => !active && handleRowClick(image)}
                onKeyDown={(e) => {
                  if (active) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleRowClick(image);
                  }
                }}
                data-testid={`datasetImageList.row.${kind}.${image.id}`}
                {...(active && { "data-active": "" })}
              >
                <Stack
                  sx={{ flexDirection: "row", alignItems: "center", gap: 1 }}
                >
                  <Tooltip
                    title={image.train ? t.TrainImage : t.TestImage}
                    arrow
                  >
                    {image.train ? (
                      <TrainIcon
                        fontSize="small"
                        sx={{
                          color: active ? "common.white" : "error.main",
                        }}
                        data-testid={`datasetImageList.splitIcon.train.${image.id}`}
                      />
                    ) : (
                      <ScienceIcon
                        fontSize="small"
                        sx={{
                          color: active ? "common.white" : "info.main",
                        }}
                        data-testid={`datasetImageList.splitIcon.test.${image.id}`}
                      />
                    )}
                  </Tooltip>
                  <Typography variant="body2" sx={{ fontWeight: "inherit" }}>
                    {`${image.region.toUpperCase()}${image.id}`}
                  </Typography>
                </Stack>
                <Stack
                  sx={{ flexDirection: "row", alignItems: "center", gap: 1 }}
                >
                  <Typography variant="caption" sx={{ color: "grey.400" }}>
                    {projectLabelMap.get(image.project_id) ??
                      t.UnknownProject(image.project_id)}
                  </Typography>
                  <Tooltip
                    title={
                      annotation
                        ? t.OpenInAnnotationToolOn(annotation.domainName)
                        : t.AnnotationToolUnavailable
                    }
                    arrow
                  >
                    <Box component="span" onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        component="a"
                        size="small"
                        href={annotation?.url ?? undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        disabled={annotation === null}
                        data-testid={`datasetImageList.openAnnotationTool.${image.id}`}
                        sx={{ color: "inherit" }}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Tooltip>
                </Stack>
              </Stack>
            );
          }}
        />
      </Box>
    </Stack>
  );
}
